import { OrderStatus } from '@prisma/client';

import { OrderSyncStatus } from './order.type';

/**
 * 🔴 Máquina de estados do pedido (D38 do PRD do MVP 1) e derivação do estado de sincronia
 * com o PDV (D41).
 *
 * Mora em `shared/types` **de propósito**: as duas regras são consumidas pelo serviço no
 * backend e pela tela no frontend, e a convenção do projeto é que contrato compartilhado vive
 * aqui (D16). Duas cópias da máquina de estados significam um `select` no painel oferecendo
 * uma transição que o serviço recusa — o operador aprendendo a regra pelo erro.
 */

/**
 * Transições permitidas, por status de origem.
 *
 * **Avanço livre para frente, sem volta** (D38): pular etapa é aceito porque o bar que serve
 * uma cerveja pronta na hora não deve pagar dois toques para marcar "Pronto", e pular não
 * perde informação. **Voltar não é aceito** — não existe histórico de status no schema, então
 * `READY → PREPARING` apagaria a única informação que havia sobre o estágio anterior.
 *
 * `CANCELED` sai de qualquer status não-final, mas é escrito por rota própria
 * (`POST /orders/:id/cancel`, `@Roles(MANAGER)`), porque é o único status que move dinheiro (D43).
 */
export const ORDER_STATUS_TRANSITIONS: Readonly<Record<OrderStatus, readonly OrderStatus[]>> = {
  [OrderStatus.PENDING]: [OrderStatus.PREPARING, OrderStatus.READY, OrderStatus.DELIVERED, OrderStatus.CANCELED],
  [OrderStatus.PREPARING]: [OrderStatus.READY, OrderStatus.DELIVERED, OrderStatus.CANCELED],
  [OrderStatus.READY]: [OrderStatus.DELIVERED, OrderStatus.CANCELED],
  [OrderStatus.DELIVERED]: [],
  [OrderStatus.CANCELED]: [],
};

/**
 * Status que o **avanço** aceita (`PATCH /orders/:id/status`). `CANCELED` fica fora: é rota
 * própria com exigência de perfil (D43), e é o `AdvanceOrderStatusDto` que trava isso no DTO.
 */
export const ORDER_STATUS_ADVANCE_TARGETS = [OrderStatus.PREPARING, OrderStatus.READY, OrderStatus.DELIVERED] as const;

export type OrderStatusAdvanceTarget = (typeof ORDER_STATUS_ADVANCE_TARGETS)[number];

/** Status final não transiciona para nada — `DELIVERED` e `CANCELED`. */
export function isFinalOrderStatus(status: OrderStatus): boolean {
  return ORDER_STATUS_TRANSITIONS[status].length === 0;
}

/**
 * Transições possíveis a partir de um status. É daqui que o `select` do card tira as opções —
 * assim ele nunca oferece o que o serviço recusa.
 */
export function allowedOrderStatusChanges(from: OrderStatus): readonly OrderStatus[] {
  return ORDER_STATUS_TRANSITIONS[from] ?? [];
}

/**
 * A pergunta que o serviço faz antes de gravar.
 *
 * ⚠️ No backend isto **não** é a garantia contra transição concorrente: a garantia é a escrita
 * condicional, com o status de origem no `where` do `updateMany` (D39). Esta função decide
 * *quais* origens são aceitas; quem serializa duas chamadas simultâneas é o banco.
 */
export function canChangeOrderStatus(from: OrderStatus, to: OrderStatus): boolean {
  return allowedOrderStatusChanges(from).includes(to);
}

/**
 * Origens aceitas para chegar a um status de destino — o `where` da escrita condicional (D39).
 * Derivado do mesmo mapa, para não existir uma segunda lista para manter em sincronia.
 */
export function allowedOrderStatusSources(to: OrderStatus): OrderStatus[] {
  return (Object.keys(ORDER_STATUS_TRANSITIONS) as OrderStatus[]).filter((from) => canChangeOrderStatus(from, to));
}

/** Aceita `Date`, ISO string e nulo — o mesmo campo é `Date` no backend e string no JSON recebido. */
type Instant = Date | string | number | null | undefined;

function toMilliseconds(instant: Instant): number | undefined {
  if (instant == null) {
    return undefined;
  }
  const time = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
  return Number.isFinite(time) ? time : undefined;
}

/**
 * Deriva o estado de sincronia com o PDV a partir de `syncedAt` e do prazo `syncDueAt`
 * calculado pelo servidor com a cfg `ORDER_SYNC_TIMEOUT_MINUTES` (D41).
 *
 * **Por que o prazo vem do servidor e a avaliação acontece nos dois lados:** o critério de
 * aceite exige que o spinner vire alerta *exatamente* no timeout configurado. Com o estado
 * resolvido só no servidor, a virada apareceria no polling seguinte — até 15s de atraso sobre
 * um timeout cujo default é 1 minuto. O cliente não conhece a cfg, então recebe o prazo pronto
 * e só compara com o relógio.
 *
 * `now >= syncDueAt` é `LATE`: no instante do prazo o timeout já decorreu.
 *
 * Sem `syncDueAt` e sem `syncedAt` (bug nosso, não estado legítimo) devolve `WAITING`: é
 * verdade — o pedido nunca sincronizou — e não produz alarme falso de "não integrado com PDV",
 * que é o tipo de ruído que faz o operador passar a ignorar o indicador.
 */
export function resolveOrderSyncStatus(
  order: { syncedAt?: Instant; syncDueAt?: Instant },
  now: Date | number = new Date(),
): OrderSyncStatus {
  if (toMilliseconds(order.syncedAt) !== undefined) {
    return 'SYNCED';
  }

  const dueAt = toMilliseconds(order.syncDueAt);
  if (dueAt === undefined) {
    return 'WAITING';
  }

  // `now` inválido cai no relógio real: melhor avaliar com a hora certa do que decidir por acaso.
  const nowInMilliseconds = toMilliseconds(now) ?? Date.now();

  return nowInMilliseconds >= dueAt ? 'LATE' : 'WAITING';
}
